import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useNotify, Title } from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { fromLocalInput, schoolApi, showTime, toLocalInput } from '../../common/schoolApi';
import { RESULT_MODES, WINDOW_COLOUR } from './constants';
import { ResultsTab } from './ResultsTab';
import { AnalysisTab } from './AnalysisTab';

/**
 * One assignment: what was set, for whom, when -- then its results.
 *
 * The API decides what may change and says why when it refuses; this page
 * offers only what can work (a draft: publish or cancel; published: a later
 * end, the results setting, the instructions; cancel while nobody started)
 * and shows the API's sentence when the answer is still no.
 */

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <Box sx={{ display: 'flex', gap: 2, py: 0.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ width: 190, flexShrink: 0 }}>
            {label}
        </Typography>
        <Box sx={{ typography: 'body2' }}>{children}</Box>
    </Box>
);

export const AssignmentShow = () => {
    const { id } = useParams();
    const notify = useNotify();
    const [a, setA] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    // ?tab=results or ?tab=analysis opens that tab, for links from the dashboard.
    const { search } = useLocation();
    const [tab, setTab] = useState(() => {
        const t = new URLSearchParams(search).get('tab');
        return t === 'results' ? 1 : t === 'analysis' ? 2 : 0;
    });

    const load = useCallback(() => {
        schoolApi
            .get(`assignment/${id}`)
            .then(r => setA(r.result))
            .catch(e => setError(e.message));
    }, [id]);
    useEffect(load, [load]);

    if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
    if (!a) return <Typography sx={{ m: 3 }}>Loading…</Typography>;

    const act = async (fn: () => Promise<any>, done: string, confirm?: string) => {
        if (confirm && !window.confirm(confirm)) return;
        try {
            await fn();
            notify(done, { type: 'success' });
            load();
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };

    const c = a.counts || {};
    return (
        <Box sx={{ p: 2 }}>
            <Title title={a.title} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ mr: 1 }}>
                    {a.title}
                </Typography>
                <Chip color={WINDOW_COLOUR[a.window_state]} label={a.window_state} />
                <Chip variant="outlined" label={a.quiz_source === 'school' ? 'School test' : 'WiZi mock'} />
                {!a.is_mine && a.assigned_by_name && (
                    <Typography variant="body2" color="text.secondary">
                        set by {a.assigned_by_name}
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {a.is_mine && a.status === 'draft' && (
                    <Button variant="contained" onClick={() => act(() => schoolApi.post(`assignment/publish/${a.id}`), 'Published.')}>
                        Publish
                    </Button>
                )}
                {a.is_mine && a.status !== 'cancelled' && (
                    <Button
                        color="error"
                        onClick={() =>
                            act(
                                () => schoolApi.post(`assignment/cancel/${a.id}`),
                                'Cancelled.',
                                'Cancel this assignment? Students will no longer see it. This cannot be undone.'
                            )
                        }
                    >
                        Cancel assignment
                    </Button>
                )}
            </Box>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Overview" />
                <Tab label={`Results (${(c.submitted || 0) + (c.auto_submitted || 0)}/${c.total || 0})`} disabled={a.status === 'draft'} />
                <Tab label="Question analysis" disabled={a.status === 'draft'} />
            </Tabs>

            {tab === 0 && <Overview a={a} onSaved={load} />}
            {tab === 1 && <ResultsTab assignment={a} onChanged={load} />}
            {tab === 2 && <AnalysisTab assignment={a} />}
        </Box>
    );
};

const Overview = ({ a, onSaved }: { a: any; onSaved: () => void }) => {
    const notify = useNotify();
    const [end, setEnd] = useState(toLocalInput(a.scheduled_end));
    const [mode, setMode] = useState(a.show_results_mode);
    const [instructions, setInstructions] = useState(a.instructions || '');
    useEffect(() => {
        setEnd(toLocalInput(a.scheduled_end));
        setMode(a.show_results_mode);
        setInstructions(a.instructions || '');
    }, [a]);

    const canChange = a.is_mine && a.status === 'published';
    const save = async () => {
        const body: any = {};
        if (end !== toLocalInput(a.scheduled_end)) body.scheduled_end = fromLocalInput(end);
        if (mode !== a.show_results_mode) body.show_results_mode = mode;
        if ((instructions || null) !== (a.instructions || null)) body.instructions = instructions;
        if (Object.keys(body).length === 0) return;
        try {
            await schoolApi.put(`assignment/${a.id}`, body);
            notify('Saved.', { type: 'success' });
            onSaved();
        } catch (e: any) {
            notify(e.message, { type: 'error' });
        }
    };

    const c = a.counts || {};
    const modeName = (RESULT_MODES.find(m => m.id === a.show_results_mode) || { name: a.show_results_mode }).name;
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 2 }}>
            <Card variant="outlined">
                <CardContent>
                    <Row label="Sections">{(a.sections || []).map((s: any) => s.label).join(', ')}</Row>
                    <Row label="Opens">{showTime(a.scheduled_start)}</Row>
                    <Row label="Closes">{showTime(a.scheduled_end)}</Row>
                    <Row label="Time for each student">{a.duration_minutes} minutes</Row>
                    <Row label="Late start allowed">
                        {a.late_start_minutes === null ? 'Any time before it closes' : `Up to ${a.late_start_minutes} minutes after it opens`}
                    </Row>
                    <Row label="Grace">{a.grace_seconds} seconds</Row>
                    <Row label="Attempts">{a.max_attempts === 1 ? 'One' : `${a.max_attempts} (the best counts)`}</Row>
                    <Row label="Students see results">
                        {modeName}
                        {a.show_results_mode === 'manual' && (a.results_released_at ? ` — released ${showTime(a.results_released_at)}` : ' — not released yet')}
                    </Row>
                    {a.instructions && <Row label="Instructions">{a.instructions}</Row>}
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" gutterBottom>
                        Students
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip label={`${c.total || 0} set`} />
                        <Chip color="info" label={`${c.in_progress || 0} writing now`} />
                        <Chip color="success" label={`${(c.submitted || 0) + (c.auto_submitted || 0)} submitted`} />
                        <Chip label={`${c.assigned || 0} not started`} />
                        {c.missed > 0 && <Chip color="warning" label={`${c.missed} missed`} />}
                        {c.excused > 0 && <Chip variant="outlined" label={`${c.excused} excused`} />}
                    </Box>
                    {a.status === 'draft' && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            A draft has no students yet. Publishing gives the test to the students in these sections at that moment.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {canChange && (
                <Card variant="outlined" sx={{ gridColumn: '1 / -1' }}>
                    <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                            Change
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Once published, students have been told the test, the start and the time. You can give them longer, change when they see
                            results, and edit the instructions. For anything else, cancel it and set a new one.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <TextField
                                size="small"
                                type="datetime-local"
                                label="Closes"
                                value={end}
                                onChange={e => setEnd(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                helperText="Later only"
                            />
                            <TextField select size="small" label="Students see results" value={mode} onChange={e => setMode(e.target.value)} sx={{ minWidth: 260 }}>
                                {RESULT_MODES.map(m => (
                                    <MenuItem key={m.id} value={m.id}>
                                        {m.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                size="small"
                                label="Instructions"
                                value={instructions}
                                onChange={e => setInstructions(e.target.value)}
                                multiline
                                sx={{ flex: 1, minWidth: 260 }}
                            />
                            <Button variant="contained" onClick={save}>
                                Save
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};
